import React from 'react';
import Modal from 'components/elements/Modal';
import useDeleteOrg from './crud/useDelete';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateDeleteErrorMessage,
  generateDeleteSuccessMessage
} from 'utils/helpers';
import InfoBox from 'utils/InfoBox';
import { colours } from 'utils/constants';
import { Button, DialogActions, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from 'components/elements/Form/Input/input';
import { useQuery, useQueryClient } from 'react-query';
import { OrganizationProps } from 'services/hooks/organization-service';

type DeleteProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteUUID: React.Dispatch<React.SetStateAction<string>>;
  deleteUUID?: string;
  deleteQuery: any;
  editQuery?: any;
  queryName: string;
};

export default function Delete({
  entityName,
  showModal,
  setShowModal,
  setDeleteUUID,
  deleteUUID,
  deleteQuery,
  editQuery,
  queryName
}: DeleteProps) {
  const queryClient = useQueryClient();

  const { data } = useQuery<OrganizationProps, Error>(
    ['organization', deleteUUID],
    () => editQuery(deleteUUID),
    {
      enabled: !!deleteUUID,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organization'));
      }
    }
  );

  const deleteOrgMutation = useDeleteOrg({
    deleteQuery,
    queryName,
    removeOnSuccess: true
  });

  const deleteOrgSchema = yup.object().shape({
    confirmationText: yup
      .string()
      .required('You must type the confirmation text.')
      .oneOf([data?.name || 'Delete Organization'], 'The confirmation text is incorrect.')
  });

  const methods = useForm({
    defaultValues: {
      confirmationText: ''
    },
    resolver: yupResolver(deleteOrgSchema)
  });

  const handleDelete = () => {
    setShowModal(false);
    toast
      .promise(() => deleteOrgMutation.mutateAsync(deleteUUID), {
        pending: 'Deleting Organization...',
        success: generateDeleteSuccessMessage('Organization'),
        error: generateDeleteErrorMessage('Organization')
      })
      .then(() => {
        queryClient.removeQueries(['organization', deleteUUID]);
        setDeleteUUID('');
      })
      .catch(() => {
        queryClient.removeQueries(['organization', deleteUUID]);
        setDeleteUUID('');
      });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (setDeleteUUID) setDeleteUUID('');
    queryClient.removeQueries(['organization', deleteUUID]);
  };

  return (
    <Modal isOpen={showModal} onRequestClose={handleCloseModal} title="Are you sure?">
      <form onSubmit={methods.handleSubmit(handleDelete)} className="text-left items-start">
        <InfoBox
          message={`Deleting ${
            data?.name ? `"${data.name}"` : 'this organization'
          } will delete all occupants, their assigned cards, amenity bookings, customer integration settings, and any admin access related to this organization.`}
        />
        <div>
          <Input
            label={`To delete this organization, please type "${
              data?.name || 'Delete Organization'
            }" in the input below.`}
            name="confirmationText"
            type="text"
            placeholder="Enter code to confirm deletion"
            autoFocus
            methods={methods}
            className="input max-w-[360px] w-full"
          />
          <p className="text-secondary2 font-bold">Note: Once deleted, this CANNOT be undone.</p>
        </div>
        <DialogActions
          sx={{
            justifyContent: { xs: 'center', sm: 'flex-start' },
            marginTop: '15px',
            paddingBottom: '0px',
            paddingLeft: '0px',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm="auto">
              <Button
                onClick={handleCloseModal}
                variant="contained"
                size="medium"
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontSize: '1rem',
                  backgroundColor: colours.seafoam,
                  color: '#fff',
                  boxShadow: 'none',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: colours.seafoam }
                }}
              >
                Keep {entityName}
              </Button>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                type="submit"
                variant="contained"
                size="medium"
                disabled={!methods.formState.isValid}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontSize: '1rem',
                  backgroundColor: colours.alertRed,
                  color: '#fff',
                  boxShadow: 'none',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: colours.alertRed },
                  '&.Mui-disabled': {
                    backgroundColor: colours.fadedAlertRed,
                    color: '#fff'
                  }
                }}
              >
                Delete {entityName}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Modal>
  );
}
